// src/pages/content/ArticlesPage.tsx

import React, { useEffect, useRef, useState } from "react";
import PageHeader from "../../components/ui/PageHeader";
import Card from "../../components/ui/Card";
import Button from "../../components/ui/Button";
import Field from "../../components/ui/Field";
import Input from "../../components/ui/Input";
import Textarea from "../../components/ui/Textarea";
import Switch from "../../components/ui/Switch";
import RichTextEditor from "../../components/ui/RichTextEditor";
import {
    getArticlesRequest,
    createArticleRequest,
    updateArticleRequest,
    deleteArticleRequest,
    uploadArticleCoverRequest,
    uploadArticlePhotoRequest,
    deleteArticlePhotoRequest,
} from "../../services/api/articles.api";
import { ArticleItem } from "../../services/types/articles.types";

const PAGE_SIZE = 10;

interface FormState {
    slug: string;
    titleRu: string;
    titleEn: string;
    titleDe: string;
    titleUa: string;
    publishedAt: string; // YYYY-MM-DD, для <input type="date">
    contentRu: string;
    contentEn: string;
    contentDe: string;
    contentUa: string;
    seoTitleRu: string;
    seoTitleEn: string;
    seoTitleDe: string;
    seoTitleUa: string;
    seoDescriptionRu: string;
    seoDescriptionEn: string;
    seoDescriptionDe: string;
    seoDescriptionUa: string;
    seoKeywordsRu: string;
    seoKeywordsEn: string;
    seoKeywordsDe: string;
    seoKeywordsUa: string;
    isPublished: boolean;
}

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const initialFormState: FormState = {
    slug: "",
    titleRu: "",
    titleEn: "",
    titleDe: "",
    titleUa: "",
    publishedAt: todayIsoDate(),
    contentRu: "",
    contentEn: "",
    contentDe: "",
    contentUa: "",
    seoTitleRu: "",
    seoTitleEn: "",
    seoTitleDe: "",
    seoTitleUa: "",
    seoDescriptionRu: "",
    seoDescriptionEn: "",
    seoDescriptionDe: "",
    seoDescriptionUa: "",
    seoKeywordsRu: "",
    seoKeywordsEn: "",
    seoKeywordsDe: "",
    seoKeywordsUa: "",
    isPublished: false,
};

const ArticlesPage: React.FC = () => {
    const [items, setItems] = useState<ArticleItem[]>([]);
    const [page, setPage] = useState(1);
    const [totalSize, setTotalSize] = useState(0);
    const [searchValue, setSearchValue] = useState("");

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [formState, setFormState] = useState<FormState>(initialFormState);
    const [coverImage, setCoverImage] = useState("");
    const [photos, setPhotos] = useState<string[]>([]);

    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isUploadingCover, setIsUploadingCover] = useState(false);
    const [isUploadingPhotos, setIsUploadingPhotos] = useState(false);
    const [isDeletingPhoto, setIsDeletingPhoto] = useState(false);
    const [pageError, setPageError] = useState<string | null>(null);
    const [formError, setFormError] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);

    const coverInputRef = useRef<HTMLInputElement | null>(null);
    const galleryInputRef = useRef<HTMLInputElement | null>(null);

    const totalPages = Math.max(1, Math.ceil(totalSize / PAGE_SIZE));

    const loadData = async () => {
        setIsLoading(true);
        setPageError(null);

        try {
            const response = await getArticlesRequest(page, PAGE_SIZE, {
                search_name: searchValue.trim() || undefined,
            });
            setItems(response.items);
            setTotalSize(response.total_size);
        } catch (error) {
            setPageError(error instanceof Error ? error.message : "Не удалось загрузить статьи");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        void loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, searchValue]);

    const resetForm = () => {
        setSelectedId(null);
        setFormState(initialFormState);
        setCoverImage("");
        setPhotos([]);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSelect = (item: ArticleItem) => {
        setSelectedId(item.id);
        setFormState({
            slug: item.slug ?? "",
            titleRu: item.title_ru,
            titleEn: item.title_en,
            titleDe: item.title_de,
            titleUa: item.title_ua ?? "",
            publishedAt: item.published_at ? item.published_at.slice(0, 10) : todayIsoDate(),
            contentRu: item.content_ru ?? "",
            contentEn: item.content_en ?? "",
            contentDe: item.content_de ?? "",
            contentUa: item.content_ua ?? "",
            seoTitleRu: item.seo_title_ru ?? "",
            seoTitleEn: item.seo_title_en ?? "",
            seoTitleDe: item.seo_title_de ?? "",
            seoTitleUa: item.seo_title_ua ?? "",
            seoDescriptionRu: item.seo_description_ru ?? "",
            seoDescriptionEn: item.seo_description_en ?? "",
            seoDescriptionDe: item.seo_description_de ?? "",
            seoDescriptionUa: item.seo_description_ua ?? "",
            seoKeywordsRu: item.seo_keywords_ru ?? "",
            seoKeywordsEn: item.seo_keywords_en ?? "",
            seoKeywordsDe: item.seo_keywords_de ?? "",
            seoKeywordsUa: item.seo_keywords_ua ?? "",
            isPublished: item.is_published,
        });
        setCoverImage(item.cover_image ?? "");
        setPhotos(item.photos ?? []);
        setFormError(null);
        setSuccessMessage(null);
    };

    const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setPage(1);
    };

    const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        setFormError(null);
        setSuccessMessage(null);

        if (!formState.titleRu.trim() || !formState.titleEn.trim() || !formState.titleDe.trim()) {
            setFormError("Заголовок обязателен на всех трёх языках (RU, EN, DE).");
            return;
        }

        if (!formState.publishedAt) {
            setFormError("Укажите дату статьи.");
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                slug: formState.slug.trim() || null,
                title_ru: formState.titleRu.trim(),
                title_en: formState.titleEn.trim(),
                title_de: formState.titleDe.trim(),
                title_ua: formState.titleUa.trim() || null,
                published_at: new Date(formState.publishedAt).toISOString(),
                content_ru: formState.contentRu || null,
                content_en: formState.contentEn || null,
                content_de: formState.contentDe || null,
                content_ua: formState.contentUa.trim() || null,
                seo_title_ru: formState.seoTitleRu.trim() || null,
                seo_title_en: formState.seoTitleEn.trim() || null,
                seo_title_de: formState.seoTitleDe.trim() || null,
                seo_title_ua: formState.seoTitleUa.trim() || null,
                seo_description_ru: formState.seoDescriptionRu.trim() || null,
                seo_description_en: formState.seoDescriptionEn.trim() || null,
                seo_description_de: formState.seoDescriptionDe.trim() || null,
                seo_description_ua: formState.seoDescriptionUa.trim() || null,
                seo_keywords_ru: formState.seoKeywordsRu.trim() || null,
                seo_keywords_en: formState.seoKeywordsEn.trim() || null,
                seo_keywords_de: formState.seoKeywordsDe.trim() || null,
                seo_keywords_ua: formState.seoKeywordsUa.trim() || null,
                is_published: formState.isPublished,
            };

            if (selectedId) {
                const updated = await updateArticleRequest(selectedId, payload);
                setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
                handleSelect(updated);
                setSuccessMessage("Статья обновлена.");
            } else {
                const created = await createArticleRequest({ ...payload, photos: [] });
                setItems((prev) => [created, ...prev]);
                setTotalSize((prev) => prev + 1);
                handleSelect(created);
                setSuccessMessage("Статья создана. Теперь можно загрузить обложку и фотографии.");
            }
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Ошибка сохранения статьи");
        } finally {
            setIsSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!selectedId) {
            return;
        }

        if (!window.confirm("Удалить статью? Она будет скрыта из списка и с сайта.")) {
            return;
        }

        setIsDeleting(true);
        setFormError(null);

        try {
            await deleteArticleRequest(selectedId);
            setItems((prev) => prev.filter((item) => item.id !== selectedId));
            setTotalSize((prev) => Math.max(0, prev - 1));
            resetForm();
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось удалить статью");
        } finally {
            setIsDeleting(false);
        }
    };

    const handleUploadCover = async (file: File | null) => {
        if (!file || !selectedId) {
            return;
        }

        setIsUploadingCover(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            const updated = await uploadArticleCoverRequest(selectedId, file);
            setCoverImage(updated.cover_image ?? "");
            setItems((prev) => prev.map((item) => (item.id === updated.id ? updated : item)));
            setSuccessMessage("Обложка успешно загружена.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить обложку");
        } finally {
            setIsUploadingCover(false);
        }
    };

    const handleUploadPhotos = async (files: FileList | null) => {
        if (!files || !selectedId) {
            return;
        }

        setIsUploadingPhotos(true);
        setFormError(null);
        setSuccessMessage(null);

        try {
            let nextPhotos = [...photos];

            for (const file of Array.from(files)) {
                const updated = await uploadArticlePhotoRequest(selectedId, file);
                nextPhotos = updated.photos ?? nextPhotos;
            }

            setPhotos(nextPhotos);
            setItems((prev) =>
                prev.map((item) => (item.id === selectedId ? { ...item, photos: nextPhotos } : item)),
            );
            setSuccessMessage("Фотографии успешно загружены.");
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось загрузить фотографии");
        } finally {
            setIsUploadingPhotos(false);
        }
    };

    const handleDeletePhoto = async (photoUrl: string) => {
        if (!selectedId) {
            return;
        }

        setIsDeletingPhoto(true);
        setFormError(null);

        try {
            const updated = await deleteArticlePhotoRequest(selectedId, photoUrl);
            const nextPhotos = updated.photos ?? photos.filter((x) => x !== photoUrl);
            setPhotos(nextPhotos);
            setItems((prev) =>
                prev.map((item) => (item.id === updated.id ? { ...item, photos: nextPhotos } : item)),
            );
        } catch (error) {
            setFormError(error instanceof Error ? error.message : "Не удалось удалить фотографию");
        } finally {
            setIsDeletingPhoto(false);
        }
    };

    const handleCoverDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        const file = event.dataTransfer.files?.[0] ?? null;
        void handleUploadCover(file);
    };

    const handleGalleryDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        void handleUploadPhotos(event.dataTransfer.files);
    };

    return (
        <div className="page-content">
            <PageHeader
                title="Статьи"
                description="Новости и статьи сайта: заголовок, дата, SEO-параметры и HTML-контент на трёх языках."
                actions={<Button onClick={resetForm}>Новая статья</Button>}
            />

            <section className="content-split">
                <Card className="content-list-card">
                    <div className="section-toolbar">
                        <div>
                            <h3 className="card-title">Список статей</h3>
                            <p className="card-text">Постраничный список, сортировка по дате статьи.</p>
                        </div>

                        <form className="section-toolbar-controls" onSubmit={handleSearchSubmit}>
                            <Input
                                type="text"
                                placeholder="Поиск по заголовку или slug..."
                                value={searchValue}
                                onChange={(event) => setSearchValue(event.target.value)}
                            />
                        </form>
                    </div>

                    {pageError ? (
                        <div className="form-global-error">{pageError}</div>
                    ) : isLoading ? (
                        <div className="table-skeleton">
                            <div className="table-skeleton-row" />
                            <div className="table-skeleton-row" />
                        </div>
                    ) : items.length === 0 ? (
                        <div className="empty-block">
                            <h3 className="empty-block-title">Статей нет</h3>
                            <p className="empty-block-text">Создайте первую статью.</p>
                        </div>
                    ) : (
                        <>
                            <div className="table-wrap">
                                <table className="data-table">
                                    <thead>
                                    <tr>
                                        <th>Заголовок (RU)</th>
                                        <th>Дата статьи</th>
                                        <th>Статус</th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    {items.map((item) => (
                                        <tr
                                            key={item.id}
                                            className={item.id === selectedId ? "data-table-row-active" : ""}
                                            onClick={() => handleSelect(item)}
                                        >
                                            <td>{item.title_ru}</td>
                                            <td>
                                                {item.published_at
                                                    ? new Date(item.published_at).toLocaleDateString("ru-RU")
                                                    : "—"}
                                            </td>
                                            <td>
                                                <span
                                                    className={`status-badge ${
                                                        item.is_published
                                                            ? "status-badge-active"
                                                            : "status-badge-inactive"
                                                    }`}
                                                >
                                                    {item.is_published ? "Опубликована" : "Черновик"}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    </tbody>
                                </table>
                            </div>

                            <div className="pagination-row">
                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={page === 1}
                                    onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                                >
                                    Назад
                                </Button>

                                <span className="pagination-text">
                                    Страница {page} из {totalPages} • всего {totalSize} статей
                                </span>

                                <Button
                                    type="button"
                                    variant="secondary"
                                    disabled={page === totalPages}
                                    onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                                >
                                    Вперёд
                                </Button>
                            </div>
                        </>
                    )}
                </Card>

                <Card className="content-form-card">
                    <div className="editor-head">
                        <div>
                            <h3 className="card-title">
                                {selectedId ? "Редактирование статьи" : "Новая статья"}
                            </h3>
                            <p className="card-text">
                                Заголовок, текст и SEO-поля заполняются отдельно на RU, EN, DE и UA (украинский — для второго сайта, необязательно).
                            </p>
                        </div>
                    </div>

                    <form className="editor-form" onSubmit={handleSubmit}>
                            <div className="form-grid form-grid-two-columns">
                                <Field label="Slug (для URL статьи)" htmlFor="article-slug">
                                    <Input
                                        id="article-slug"
                                        placeholder="naprimer-nazvanie-statyi"
                                        value={formState.slug}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, slug: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="Дата статьи" htmlFor="article-published-at">
                                    <Input
                                        id="article-published-at"
                                        type="date"
                                        value={formState.publishedAt}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                publishedAt: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>
                            </div>

                            <div className="form-grid form-grid-two-columns">
                                <Field label="Заголовок (RU)" htmlFor="article-title-ru">
                                    <Input
                                        id="article-title-ru"
                                        value={formState.titleRu}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, titleRu: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="Заголовок (EN)" htmlFor="article-title-en">
                                    <Input
                                        id="article-title-en"
                                        value={formState.titleEn}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, titleEn: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="Заголовок (DE)" htmlFor="article-title-de">
                                    <Input
                                        id="article-title-de"
                                        value={formState.titleDe}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, titleDe: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="Заголовок (UA)" htmlFor="article-title-ua">
                                    <Input
                                        id="article-title-ua"
                                        value={formState.titleUa}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, titleUa: event.target.value }))
                                        }
                                    />
                                </Field>
                            </div>

                            <RichTextEditor
                                label="Текст статьи (RU)"
                                value={formState.contentRu}
                                onChange={(value) => setFormState((prev) => ({ ...prev, contentRu: value }))}
                                minHeight={220}
                            />

                            <RichTextEditor
                                label="Текст статьи (EN)"
                                value={formState.contentEn}
                                onChange={(value) => setFormState((prev) => ({ ...prev, contentEn: value }))}
                                minHeight={220}
                            />

                            <RichTextEditor
                                label="Текст статьи (DE)"
                                value={formState.contentDe}
                                onChange={(value) => setFormState((prev) => ({ ...prev, contentDe: value }))}
                                minHeight={220}
                            />

                            <RichTextEditor
                                label="Текст статьи (UA)"
                                value={formState.contentUa}
                                onChange={(value) => setFormState((prev) => ({ ...prev, contentUa: value }))}
                                minHeight={220}
                            />

                            <div className="editor-head" style={{ marginTop: 24 }}>
                                <div>
                                    <h3 className="card-title">SEO</h3>
                                    <p className="card-text">
                                        Meta title, meta description и ключевые слова для поисковых систем — отдельно
                                        для каждого языка. Если поля не заполнены, сайт может использовать обычный
                                        заголовок статьи.
                                    </p>
                                </div>
                            </div>

                            <div className="form-grid form-grid-two-columns">
                                <Field label="SEO Title (RU)" htmlFor="article-seo-title-ru">
                                    <Input
                                        id="article-seo-title-ru"
                                        value={formState.seoTitleRu}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, seoTitleRu: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Title (EN)" htmlFor="article-seo-title-en">
                                    <Input
                                        id="article-seo-title-en"
                                        value={formState.seoTitleEn}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, seoTitleEn: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Title (DE)" htmlFor="article-seo-title-de">
                                    <Input
                                        id="article-seo-title-de"
                                        value={formState.seoTitleDe}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, seoTitleDe: event.target.value }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Title (UA)" htmlFor="article-seo-title-ua">
                                    <Input
                                        id="article-seo-title-ua"
                                        value={formState.seoTitleUa}
                                        onChange={(event) =>
                                            setFormState((prev) => ({ ...prev, seoTitleUa: event.target.value }))
                                        }
                                    />
                                </Field>
                            </div>

                            <div className="form-grid form-grid-two-columns">
                                <Field label="SEO Description (RU)" htmlFor="article-seo-desc-ru">
                                    <Textarea
                                        id="article-seo-desc-ru"
                                        rows={3}
                                        value={formState.seoDescriptionRu}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoDescriptionRu: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Description (EN)" htmlFor="article-seo-desc-en">
                                    <Textarea
                                        id="article-seo-desc-en"
                                        rows={3}
                                        value={formState.seoDescriptionEn}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoDescriptionEn: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Description (DE)" htmlFor="article-seo-desc-de">
                                    <Textarea
                                        id="article-seo-desc-de"
                                        rows={3}
                                        value={formState.seoDescriptionDe}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoDescriptionDe: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Description (UA)" htmlFor="article-seo-desc-ua">
                                    <Textarea
                                        id="article-seo-desc-ua"
                                        rows={3}
                                        value={formState.seoDescriptionUa}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoDescriptionUa: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>
                            </div>

                            <div className="form-grid form-grid-two-columns">
                                <Field label="SEO Keywords (RU)" htmlFor="article-seo-keywords-ru">
                                    <Input
                                        id="article-seo-keywords-ru"
                                        placeholder="ключевое слово, ещё одно"
                                        value={formState.seoKeywordsRu}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoKeywordsRu: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Keywords (EN)" htmlFor="article-seo-keywords-en">
                                    <Input
                                        id="article-seo-keywords-en"
                                        value={formState.seoKeywordsEn}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoKeywordsEn: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Keywords (DE)" htmlFor="article-seo-keywords-de">
                                    <Input
                                        id="article-seo-keywords-de"
                                        value={formState.seoKeywordsDe}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoKeywordsDe: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>

                                <Field label="SEO Keywords (UA)" htmlFor="article-seo-keywords-ua">
                                    <Input
                                        id="article-seo-keywords-ua"
                                        value={formState.seoKeywordsUa}
                                        onChange={(event) =>
                                            setFormState((prev) => ({
                                                ...prev,
                                                seoKeywordsUa: event.target.value,
                                            }))
                                        }
                                    />
                                </Field>
                            </div>

                            <div className="form-switch-row">
                                <Switch
                                    checked={formState.isPublished}
                                    onChange={(checked) =>
                                        setFormState((prev) => ({ ...prev, isPublished: checked }))
                                    }
                                    label={formState.isPublished ? "Статья опубликована" : "Черновик (скрыта на сайте)"}
                                />
                            </div>

                            {formError ? <div className="form-global-error">{formError}</div> : null}
                            {successMessage ? <div className="form-global-success">{successMessage}</div> : null}

                            <div className="editor-actions">
                                <Button type="submit" disabled={isSaving}>
                                    {isSaving ? "Сохраняем..." : selectedId ? "Сохранить изменения" : "Создать статью"}
                                </Button>
                                <Button type="button" variant="secondary" onClick={resetForm}>
                                    Очистить форму
                                </Button>
                                {selectedId ? (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        onClick={handleDelete}
                                        disabled={isDeleting}
                                    >
                                        {isDeleting ? "Удаляем..." : "Удалить статью"}
                                    </Button>
                                ) : null}
                            </div>
                        </form>

                    {selectedId ? (
                        <>
                            <Card className="hall-gallery-card">
                                <div className="editor-head">
                                    <div>
                                        <h3 className="card-title">Обложка статьи</h3>
                                        <p className="card-text">
                                            Загрузка обложки доступна только для сохранённой статьи.
                                        </p>
                                    </div>
                                </div>

                                <div className="hall-gallery-upload">
                                    <div
                                        className="hall-gallery-dropzone"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => coverInputRef.current?.click()}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                coverInputRef.current?.click();
                                            }
                                        }}
                                        onDragEnter={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            event.dataTransfer.dropEffect = "copy";
                                        }}
                                        onDrop={handleCoverDrop}
                                    >
                                        <input
                                            ref={coverInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hall-gallery-file-input"
                                            onChange={(event) => {
                                                void handleUploadCover(event.target.files?.[0] ?? null);
                                                event.currentTarget.value = "";
                                            }}
                                        />
                                        <span className="hall-gallery-dropzone-title">Выберите обложку</span>
                                        <span className="hall-gallery-dropzone-text">
                                            Перетащите файл сюда или нажмите для выбора.
                                        </span>
                                    </div>
                                </div>

                                {isUploadingCover ? (
                                    <div className="form-global-success">Загружаем обложку...</div>
                                ) : null}

                                {coverImage ? (
                                    <div className="hall-photos-grid">
                                        <div className="hall-photo-card">
                                            <img src={coverImage} alt="Обложка статьи" className="hall-photo-preview" />
                                        </div>
                                    </div>
                                ) : (
                                    <div className="empty-block">
                                        <h3 className="empty-block-title">Обложка не загружена</h3>
                                        <p className="empty-block-text">После загрузки здесь появится превью.</p>
                                    </div>
                                )}
                            </Card>

                            <Card className="hall-gallery-card">
                                <div className="editor-head">
                                    <div>
                                        <h3 className="card-title">Фотографии статьи</h3>
                                        <p className="card-text">Можно загрузить сразу несколько фотографий.</p>
                                    </div>
                                </div>

                                <div className="hall-gallery-upload">
                                    <div
                                        className="hall-gallery-dropzone"
                                        role="button"
                                        tabIndex={0}
                                        onClick={() => galleryInputRef.current?.click()}
                                        onKeyDown={(event) => {
                                            if (event.key === "Enter" || event.key === " ") {
                                                event.preventDefault();
                                                galleryInputRef.current?.click();
                                            }
                                        }}
                                        onDragEnter={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        onDragOver={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                            event.dataTransfer.dropEffect = "copy";
                                        }}
                                        onDrop={handleGalleryDrop}
                                    >
                                        <input
                                            ref={galleryInputRef}
                                            type="file"
                                            multiple
                                            accept="image/*"
                                            className="hall-gallery-file-input"
                                            onChange={(event) => {
                                                void handleUploadPhotos(event.target.files);
                                                event.currentTarget.value = "";
                                            }}
                                        />
                                        <span className="hall-gallery-dropzone-title">
                                            Перетащите фото сюда или нажмите для выбора
                                        </span>
                                        <span className="hall-gallery-dropzone-text">
                                            Изображения загружаются по одному и затем появляются ниже.
                                        </span>
                                    </div>
                                </div>

                                {isUploadingPhotos ? (
                                    <div className="form-global-success">Загружаем фотографии...</div>
                                ) : null}

                                {photos.length > 0 ? (
                                    <div className="hall-photos-grid">
                                        {photos.map((photoUrl, index) => (
                                            <div key={`${photoUrl}-${index}`} className="hall-photo-card">
                                                <img
                                                    src={photoUrl}
                                                    alt={`Фото статьи ${index + 1}`}
                                                    className="hall-photo-preview"
                                                />
                                                <button
                                                    type="button"
                                                    className="hall-photo-delete"
                                                    onClick={() => {
                                                        void handleDeletePhoto(photoUrl);
                                                    }}
                                                    disabled={isDeletingPhoto}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <div className="empty-block">
                                        <h3 className="empty-block-title">Фотографий пока нет</h3>
                                        <p className="empty-block-text">
                                            После загрузки здесь появятся превью с возможностью удаления.
                                        </p>
                                    </div>
                                )}
                            </Card>
                        </>
                    ) : null}
                </Card>
            </section>
        </div>
    );
};

export default ArticlesPage;
